import React, { useEffect } from 'react'
import { Card } from '@mui/material'
import Condition from 'components/Condition'
import PageContainer from 'components/PageContainer'
import PageHeader from 'components/PageHeader'
import PageWrapper from 'components/PageWrapper'
import Suspense from 'components/Suspense'
import FreelancerProfileComponent from 'components/FreelancerProfile'
import { graphql, useFragment, useQueryLoader } from 'react-relay'
import { FreelancerProfile_UserScene$key } from '__generated__/FreelancerProfile_UserScene.graphql'
import { ProfileActions_Query } from '__generated__/ProfileActions_Query.graphql'
import { VanityUrlMessage_Query } from '__generated__/VanityUrlMessage_Query.graphql'
import { PageLoadingIndicator } from 'components/Layouts/V3'
import { useQueryParams } from 'hooks/router'
import useStyles from './styles'
import ProfileActions, { ProfileActionsQuery } from './components/ProfileActions'
import FreelancerProfileHead from './components/FreelancerProfileHead'
import VanityUrl, { VanityUrlMessageQuery } from './components/VanityUrlMessage'
import FreelancerProfileSelfActions from './components/FreelancerProfileSelfActions'

export interface IFreelancerProfileProps {
  user: FreelancerProfile_UserScene$key
}

/**
 * This component is the page that displays a profile
 * It's mainly used by SlugViewer to display a profile when /slug visited
 * and the slug matches a profile.
 */
function FreelancerProfile({ user: userFragmentRef }: IFreelancerProfileProps) {
  const { classes } = useStyles()
  const query = useQueryParams()
  const isInSignupFlow = query?.action === 'signup'
  const jobId = query?.job

  const user = useFragment(graphql`
    # TODO: fix fragment name and duplicate definition of this component
    fragment FreelancerProfile_UserScene on User {
      id
      self
      ...FreelancerProfileHead_User
      ...FreelancerProfile_User
    }
  `, userFragmentRef)

  const [profileActionsQuery, loadProfileActions] = useQueryLoader<ProfileActions_Query>(ProfileActionsQuery)
  const [vanityUrlQuery, loadVanityUrl] = useQueryLoader<VanityUrlMessage_Query>(VanityUrlMessageQuery)

  useEffect(() => {
    if (user?.id) {
      loadProfileActions({ userId: user.id })
      loadVanityUrl({ userId: user.id })
    }
  }, [user?.id])

  return (
    <div>
      <FreelancerProfileHead userFragmentRef={user} />

      <Suspense>
        <PageHeader compact />
      </Suspense>

      <Suspense>
        <PageContainer className={classes.page}>
          <Condition condition={user?.self}>
            <Suspense>
              <FreelancerProfileSelfActions />
            </Suspense>
          </Condition>

          <PageWrapper withoutCard noAnim cardStyle={{ overflow: 'visible' }}>
            <Card raised>
              <Suspense fallback={<PageLoadingIndicator />}>
                <FreelancerProfileComponent
                  user={user}
                  editable={false}
                  showTimelineErrorBadge={false}
                >
                  {profileActionsQuery && (
                    <Suspense>
                      <ProfileActions
                        isInSignupFlow={isInSignupFlow}
                        preloadedQuery={profileActionsQuery}
                        jobId={jobId}
                      />
                    </Suspense>
                  )}
                </FreelancerProfileComponent>
              </Suspense>
            </Card>
          </PageWrapper>

          {vanityUrlQuery && <Suspense><VanityUrl preloadedQuery={vanityUrlQuery} /></Suspense>}
        </PageContainer>
      </Suspense>
    </div>
  )
}

export default React.memo(FreelancerProfile)
